// Plain data module (no 'use client', no JSX) — the single source of truth
// for brand/dashboard structure, shared between BackofficeApp.tsx (renders
// the sidebar + routes views) and ManageTenantsView.tsx (renders the
// per-tenant Dashboard Access matrix). Keeping it here avoids one importing
// from the other just to reach this data. It's also what lets Server
// Components (e.g. /[brand]/[dashboard]) import VIEW_SLUGS below without
// pulling in BackofficeApp.tsx's client bundle (recharts, supertokens-auth-
// react, …) — see entriesRoutes.ts's ENTRIES_SLUGS for the same reasoning.

import { ENTRIES_SLUGS } from './entriesRoutes'

export type View =
  | 'sibs-integrity' | 'ctt-integrity' | 'santander-integrity' | 'cgd-integrity' | 'sixt-integrity'
  | 'sibs-overview' | 'sibs-mail' | 'sibs-mail-entries' | 'sibs-alerts' | 'sibs-pilot-overview' | 'sibs-cases' | 'sibs-unified' | 'sibs-domains'
  | 'ctt-mail-alert' | 'ctt-processed' | 'ctt-scalls' | 'ctt-site' | 'ctt-trustcenter' | 'ctt-trustcenter-entries' | 'ctt-cases' | 'ctt-unified' | 'ctt-domains' | 'ctt-raw-inbound-email' | 'ctt-vendor-entries'
  | 'santander-overview' | 'santander-processed' | 'santander-cases' | 'santander-unified' | 'santander-domains'
  | 'cgd-overview' | 'cgd-processed' | 'cgd-cases' | 'cgd-unified' | 'cgd-domains'
  | 'meo-integrity' | 'meo-cases' | 'meo-unified' | 'meo-domains' | 'meo-trustcenter-entries'
  | 'sixt-cases' | 'sixt-unified' | 'sixt-domains'
  | 'entries-cases' | 'entries-casos' | 'entries-unified' | 'entries-campaigns' | 'entries-domains' | 'entries-plataformas-manage' | 'entries-brand-vocabulary' | 'entries-token-chain' | 'entries-sources-manage' | 'entries-tenants-manage' | 'entries-tenant-logs' | 'entries-email-types' | 'entries-email-logs' | 'entries-email-compose' | 'entries-submit-entry' | 'entries-jobs' | 'entries-actions' | 'entries-calls-godview' | 'entries-ui-kit'

// 'shared' = grantable to a tenant at all — offered as a checkbox in Manage
// Tenants' Dashboard Access matrix (ManageTenantsView.tsx), where an admin
// grants it to zero, one, or several tenants explicitly (tenant_dashboard_access
// table). Access is purely that grant — there's no implicit tenant/platform
// matching. 'exclusive' (the default when omitted) = Editor-only, never
// offered in the matrix at all, even though it may live in the sidebar right
// alongside 'shared' views under the same brand.
export type ViewAccess = 'shared' | 'exclusive'
// sharedTemplate marks a per-brand entry that's really the same general
// dashboard component reused across every brand (e.g. each brand's own
// "Unified Entries", all rendering UnifiedEntriesDashboardView just pre-scoped to that brand's
// platform_id) rather than something bespoke to this brand — the sidebar
// renders a small link icon next to these so that's visible at a glance,
// instead of looking like a one-off SIBS/CTT/… feature.
export type FlatView = { id: View; label: string; access?: ViewAccess; children?: FlatView[]; sharedTemplate?: boolean }
// items is SidebarEntry[] (not FlatView[]) so a group can itself contain
// sub-groups — e.g. CTT's "Raw" group split by data source below. Only one
// level of nesting is actually used today, but flatViews/grantedOnlyEntries
// both recurse, so deeper nesting works too if it's ever needed.
export type ViewGroup = { group: string; items: SidebarEntry[] }
export type SidebarEntry = FlatView | ViewGroup
export function isGroup(e: SidebarEntry): e is ViewGroup { return 'group' in e }
export function flatViews(views: SidebarEntry[]): FlatView[] {
  return views.flatMap((e) => isGroup(e) ? flatViews(e.items) : [e, ...(e.children ?? [])])
}

// platformNameCode is the row in the platform table this brand scopes to, and
// it is NOT always the sidebar id: SIBS' platform is name_code 'mbway'. It is
// what brandsForPlatforms below dedupes against, so a brand with a bespoke
// entry here is never also generated as a default one.
export type BrandEntry = {
  id: string; label: string; platformCode: string; views: SidebarEntry[]
  platformNameCode?: string
  /** true when this brand was generated from a platform row rather than written below. */
  generated?: boolean
}

export const BRANDS: BrandEntry[] = [
  { id: 'sibs', label: 'SIBS', platformCode: 'sibs', platformNameCode: 'mbway', views: [
    { group: 'Default Dashboards', items: [
      { id: 'sibs-integrity', label: 'Brand Integrity', access: 'shared', sharedTemplate: true },
      { id: 'sibs-unified', label: 'Unified Entries', access: 'shared', sharedTemplate: true, children: [
        { id: 'sibs-cases', label: 'Old Cases', access: 'shared', sharedTemplate: true },
      ]},
      { id: 'sibs-domains', label: 'Domain Candidates', access: 'shared', sharedTemplate: true },
    ]},
    { id: 'sibs-overview', label: 'Overview', access: 'shared' },
    { id: 'sibs-mail', label: 'Custom Mail Alert', access: 'shared', children: [
      { id: 'sibs-mail-entries', label: 'Entries', access: 'shared' },
    ]},
    { id: 'sibs-alerts', label: 'Alerts Analysis', access: 'shared' },
    { id: 'sibs-pilot-overview', label: 'Pilot Overview', access: 'shared' },
  ]},
  { id: 'ctt', label: 'CTT', platformCode: 'ctt', views: [
    { group: 'Default Dashboards', items: [
      { id: 'ctt-integrity', label: 'Brand Integrity', access: 'shared', sharedTemplate: true },
      { id: 'ctt-unified', label: 'Unified Entries', access: 'shared', sharedTemplate: true, children: [
        { id: 'ctt-cases', label: 'Old Cases', access: 'shared', sharedTemplate: true },
      ]},
      { id: 'ctt-domains', label: 'Domain Candidates', access: 'shared', sharedTemplate: true },
    ]},
    { id: 'ctt-vendor-entries', label: 'Pilot Overview', access: 'shared' },
    { group: 'Raw', items: [
      { group: 'Source Trust Fence', items: [
        { id: 'ctt-mail-alert', label: 'Custom Mail Alert', access: 'shared' },
        { id: 'ctt-processed', label: 'Old Processed Entries', access: 'shared' },
        { id: 'ctt-scalls', label: 'Old AI Signals', access: 'shared' },
      ]},
      // Every entry with source "ctt_trustfence_email" (routes/cloudflareWorkers/
      // brands/ctt.ts), unlike the rest of this group — not scoped to entries
      // already attributed to CTT's platform_id. Catches inbound emails that came
      // in through CTT's own channel but got mis-attributed (or never attributed)
      // to another/no platform, so they'd otherwise never show up anywhere under CTT.
      // Own subgroup (not a flat item) so other brands' inbound-email channels can
      // land as sibling entries here later instead of this being CTT-only forever.
      { group: 'Source Inbound', items: [
        { id: 'ctt-raw-inbound-email', label: 'CTT Email', access: 'shared' },
      ]},
    ]},
    { group: 'Mockup', items: [
      { id: 'ctt-trustcenter-entries', label: 'Entries', access: 'shared' },
      { id: 'ctt-trustcenter', label: 'Old entries', access: 'shared' },
      { id: 'ctt-site', label: 'Preview', access: 'shared' },
    ]},
  ]},
  { id: 'santander', label: 'Santander', platformCode: 'santander', views: [
    { group: 'Default Dashboards', items: [
      { id: 'santander-integrity', label: 'Brand Integrity', access: 'shared', sharedTemplate: true },
      { id: 'santander-unified', label: 'Unified Entries', access: 'shared', sharedTemplate: true, children: [
        { id: 'santander-cases', label: 'Old Cases', access: 'shared', sharedTemplate: true },
      ]},
      { id: 'santander-domains', label: 'Domain Candidates', access: 'shared', sharedTemplate: true },
    ]},
    { id: 'santander-overview', label: 'Overview', access: 'shared' },
    { id: 'santander-processed', label: 'Processed Entries', access: 'shared' },
  ]},
  { id: 'cgd', label: 'CGD', platformCode: 'cgd', views: [
    { group: 'Default Dashboards', items: [
      { id: 'cgd-integrity', label: 'Brand Integrity', access: 'shared', sharedTemplate: true },
      { id: 'cgd-unified', label: 'Unified Entries', access: 'shared', sharedTemplate: true, children: [
        { id: 'cgd-cases', label: 'Old Cases', access: 'shared', sharedTemplate: true },
      ]},
      { id: 'cgd-domains', label: 'Domain Candidates', access: 'shared', sharedTemplate: true },
    ]},
    { id: 'cgd-overview', label: 'Overview', access: 'shared' },
    { id: 'cgd-processed', label: 'Processed Entries', access: 'shared' },
  ]},
  // MEO was a generated brand until 2026-10-29; written out so it can carry
  // a Mockup group like CTT's. Its campaigns come from the scheduled scraper
  // of MEO's own fraud-alerts forum (jupyter-fraud brand_alerts, entry-first:
  // every campaign is a projection of an auto-accepted scam entry), so the
  // Mockup Entries view is where those land. No Preview item: unlike CTT
  // there is no MEO mockup site to iframe yet.
  { id: 'meo', label: 'MEO', platformCode: 'meo', views: [
    { group: 'Default Dashboards', items: [
      { id: 'meo-integrity', label: 'Brand Integrity', access: 'shared', sharedTemplate: true },
      { id: 'meo-unified', label: 'Unified Entries', access: 'shared', sharedTemplate: true, children: [
        { id: 'meo-cases', label: 'Old Cases', access: 'shared', sharedTemplate: true },
      ]},
      { id: 'meo-domains', label: 'Domain Candidates', access: 'shared', sharedTemplate: true },
    ]},
    { group: 'Mockup', items: [
      { id: 'meo-trustcenter-entries', label: 'Entries', access: 'shared' },
    ]},
  ]},
  // Sixt has no bespoke views yet — only the shared per-brand templates,
  // pre-scoped to its platform_id (resolved from the platform table by
  // name_code 'sixt' at runtime; the id differs per environment).
  { id: 'sixt', label: 'Sixt', platformCode: 'sixt', views: [
    { group: 'Default Dashboards', items: [
      { id: 'sixt-integrity', label: 'Brand Integrity', access: 'shared', sharedTemplate: true },
      { id: 'sixt-unified', label: 'Unified Entries', access: 'shared', sharedTemplate: true, children: [
        { id: 'sixt-cases', label: 'Old Cases', access: 'shared', sharedTemplate: true },
      ]},
      { id: 'sixt-domains', label: 'Domain Candidates', access: 'shared', sharedTemplate: true },
    ]},
  ]},
]

// Every platform gets these four dashboards, whether or not anyone wrote it a
// BRANDS entry above: they are the shared templates pre-scoped to a
// platform_id, and nothing in them is brand-specific. BRANDS stays the list of
// brands that ALSO have bespoke views (SIBS' Alerts Analysis, CTT's Raw
// groups) -- being in it is no longer what gets a brand into the sidebar.
// integrity = Brand Integrity (Entries/BrandIntegrityView): the brand's
// standing -- secure / elevated / under threat -- from its own entries.
export const DEFAULT_BRAND_SLUGS = { integrity: 'brand-integrity', cases: 'cases', unified: 'unified', domains: 'domain-candidates' } as const
export type DefaultBrandKind = keyof typeof DEFAULT_BRAND_SLUGS
export const DEFAULT_BRAND_SLUG_SET: ReadonlySet<string> = new Set(Object.values(DEFAULT_BRAND_SLUGS))

// Generated ids follow the same '<code>-cases' convention the hand-written
// ones use, so everything downstream (slugs, routes, access grants) treats
// them identically. They are cast to View because View is a closed union of
// the ids known at build time and these are not knowable until the platform
// table is read -- slugForView below is what keeps lookups working for both.
export function defaultBrandViews(code: string): SidebarEntry[] {
  return [{ group: 'Default Dashboards', items: [
    { id: `${code}-integrity` as View, label: 'Brand Integrity', access: 'shared', sharedTemplate: true },
    { id: `${code}-unified` as View, label: 'Unified Entries', access: 'shared', sharedTemplate: true, children: [
      { id: `${code}-cases` as View, label: 'Old Cases', access: 'shared', sharedTemplate: true },
    ] },
    { id: `${code}-domains` as View, label: 'Domain Candidates', access: 'shared', sharedTemplate: true },
  ] }]
}

export type CatalogPlatform = { id: number; name_code: string; name: string; tenant_ids?: string[]; is_public?: boolean }

/** BRANDS, plus a default-dashboards brand for every other platform that is
 *  assigned to at least one tenant (tenant_platforms, carried on the catalog
 *  as tenant_ids). The platform table is a long tail of brands seen once in an
 *  entry -- Netflix, Temu, Telegram -- and a sidebar listing all 48 of them
 *  would bury the handful anyone actually works on. Being assigned to a tenant
 *  is the existing, editable statement that someone is responsible for a
 *  brand, so it is what decides. Hand-written BRANDS are never filtered: they
 *  are an explicit decision that predates this. */
export function brandsForPlatforms(platforms: ReadonlyArray<CatalogPlatform>): BrandEntry[] {
  // Every name a hand-written brand answers to, not just the platform it
  // scopes to: SIBS scopes to name_code 'mbway' but there is ALSO a 'sibs'
  // platform row, and generating a brand for it would produce a second entry
  // with the id 'sibs' -- a duplicate sidebar entry and an ambiguous route.
  const covered = new Set(BRANDS.flatMap((b) => [b.id, b.platformCode, b.platformNameCode ?? b.platformCode]))
  const extra = platforms
    .filter((p) => p.name_code && !covered.has(p.name_code) && (p.tenant_ids?.length ?? 0) > 0)
    .map((p): BrandEntry => ({
      id: p.name_code, label: p.name || p.name_code, platformCode: p.name_code,
      platformNameCode: p.name_code, views: defaultBrandViews(p.name_code), generated: true,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
  return [...BRANDS, ...extra]
}

/** A view's URL slug. Generated views are not in VIEW_SLUGS, so fall back to
 *  the suffix -- 'viaverde-domains' is 'domain-candidates' like every other. */
export function slugForView(view: View): string {
  const known = (VIEW_SLUGS as Record<string, string | undefined>)[view]
  if (known) return known
  const suffix = String(view).split('-').pop() as DefaultBrandKind
  return DEFAULT_BRAND_SLUGS[suffix] ?? String(view)
}

/** The brand code a per-brand view belongs to: 'viaverde-domains' -> 'viaverde'. */
export function brandCodeForView(view: View): string {
  return String(view).replace(/-(integrity|cases|unified|domains)$/, '')
}

export function defaultKindForView(view: View): DefaultBrandKind | null {
  const m = /-(integrity|cases|unified|domains)$/.exec(String(view))
  return m ? (m[1] as DefaultBrandKind) : null
}

// The catalog rendered as checkboxes in Manage Tenants' Dashboard Access
// matrix — every 'shared' view across every brand, flattened with a label.
// sectionLabel carries the nearest enclosing sidebar group (e.g. "Default
// Dashboards", "Mockup") so the matrix can sub-group grants the same way the
// sidebar does, instead of losing that structure by flattening through it.
export type DashboardCatalogEntry = { key: View; label: string; brandLabel: string; sectionLabel?: string }
function grantableEntries(items: SidebarEntry[], brandLabel: string, sectionLabel?: string): DashboardCatalogEntry[] {
  return items.flatMap((item) =>
    isGroup(item)
      ? grantableEntries(item.items, brandLabel, item.group)
      : [item, ...(item.children ?? [])]
          .filter((v) => v.access === 'shared')
          .map((v) => ({ key: v.id, label: v.label, brandLabel, sectionLabel })),
  )
}
export const GRANTABLE_DASHBOARDS: DashboardCatalogEntry[] = BRANDS.flatMap((b) => grantableEntries(b.views, b.label))

/** Same matrix, including the brands generated from platform rows -- what
 *  Manage Tenants must offer, or a generated brand could never be granted to
 *  a tenant and would be editor-only forever. */
export function grantableDashboards(brands: ReadonlyArray<BrandEntry>): DashboardCatalogEntry[] {
  return brands.flatMap((b) => grantableEntries(b.views, b.label))
}

// Every View's URL slug (the last path segment under /[brand]/[dashboard]).
// Single source of truth for both BackofficeApp.tsx's own pushState
// navigation (viewHref) and the server-side allowlist in
// app/[brand]/[dashboard]/page.tsx that validates a reload's URL — that page
// used to hardcode its own separate per-brand slug Set, which drifted out of
// sync whenever a view was added here (e.g. ctt-mail-alert was missing from
// it), silently redirecting a reload on that view back to /[brand]/overview.
export const VIEW_SLUGS: Record<View, string> = {
  'sibs-overview': 'overview',
  'sibs-mail': 'mail',
  'sibs-mail-entries': 'mail-entries',
  'sibs-alerts': 'alerts-analysis',
  'sibs-pilot-overview': 'pilot-overview',
  'sibs-integrity': 'brand-integrity',
  'sibs-cases': 'cases',
  'sibs-unified': 'unified',
  'sibs-domains': 'domain-candidates',
  'ctt-mail-alert': 'mail-alert',
  'ctt-processed': 'processed-entries',
  'ctt-scalls': 'ai-signals',
  'ctt-site': 'preview',
  'ctt-trustcenter': 'trust-center-old',
  'ctt-trustcenter-entries': 'mockup-entries',
  'ctt-integrity': 'brand-integrity',
  'ctt-cases': 'cases',
  'ctt-unified': 'unified',
  'ctt-domains': 'domain-candidates',
  'ctt-raw-inbound-email': 'raw-inbound-email',
  'ctt-vendor-entries': 'pilot-overview',
  'santander-overview': 'overview',
  'santander-processed': 'processed-entries',
  'santander-integrity': 'brand-integrity',
  'santander-cases': 'cases',
  'santander-unified': 'unified',
  'santander-domains': 'domain-candidates',
  'meo-integrity': 'brand-integrity',
  'meo-cases': 'cases',
  'meo-unified': 'unified',
  'meo-domains': 'domain-candidates',
  'meo-trustcenter-entries': 'mockup-entries',
  'cgd-overview': 'overview',
  'cgd-processed': 'processed-entries',
  'cgd-integrity': 'brand-integrity',
  'cgd-cases': 'cases',
  'cgd-unified': 'unified',
  'cgd-domains': 'domain-candidates',
  'sixt-integrity': 'brand-integrity',
  'sixt-cases': 'cases',
  'sixt-unified': 'unified',
  'sixt-domains': 'domain-candidates',
  'entries-cases': ENTRIES_SLUGS['entries-cases'],
  'entries-casos': ENTRIES_SLUGS['entries-casos'],
  'entries-unified': ENTRIES_SLUGS['entries-unified'],
  'entries-campaigns': ENTRIES_SLUGS['entries-campaigns'],
  'entries-domains': ENTRIES_SLUGS['entries-domains'],
  'entries-plataformas-manage': ENTRIES_SLUGS['entries-plataformas-manage'],
  'entries-brand-vocabulary': ENTRIES_SLUGS['entries-brand-vocabulary'],
  'entries-token-chain': ENTRIES_SLUGS['entries-token-chain'],
  'entries-sources-manage': ENTRIES_SLUGS['entries-sources-manage'],
  'entries-tenants-manage': ENTRIES_SLUGS['entries-tenants-manage'],
  'entries-tenant-logs': ENTRIES_SLUGS['entries-tenant-logs'],
  'entries-email-types': ENTRIES_SLUGS['entries-email-types'],
  'entries-email-logs': ENTRIES_SLUGS['entries-email-logs'],
  'entries-email-compose': ENTRIES_SLUGS['entries-email-compose'],
  'entries-submit-entry': ENTRIES_SLUGS['entries-submit-entry'],
  'entries-jobs': ENTRIES_SLUGS['entries-jobs'],
  'entries-actions': ENTRIES_SLUGS['entries-actions'],
  'entries-calls-godview': ENTRIES_SLUGS['entries-calls-godview'],
  'entries-ui-kit': ENTRIES_SLUGS['entries-ui-kit'],
}
